import { useMemo } from 'react';
import type { InternalColumnDef } from '../core/schema/types';
import type { ValidationCoalesceRule } from '../core/validation/coerce';
import { hasSchemaValidation, validateRowFromSchema } from '../core/validation/validateRowFromSchema';

export type RowValidationFn = (
  row: Record<string, unknown>,
  rowKey?: string,
) => Record<string, string>;

export function createRowValidator(
  columns: ReadonlyArray<InternalColumnDef>,
  escapeHatch?: (row: Record<string, unknown>) => Record<string, string>,
  validationCoalesce?: ReadonlyArray<ValidationCoalesceRule> | null,
): RowValidationFn | undefined {
  const hasSchema = hasSchemaValidation(columns);
  if (!hasSchema && !escapeHatch) return undefined;

  return (row: Record<string, unknown>) => ({
    ...(escapeHatch?.(row) ?? {}),
    ...(hasSchema ? validateRowFromSchema(columns, row, validationCoalesce) : {}),
  });
}

export type CrossRowValidationFn = (
  row: Record<string, unknown>,
  rowKey: string,
  allRows: unknown[],
) => Record<string, string>;

interface UseTableValidationParams {
  parentColumns: ReadonlyArray<InternalColumnDef>;
  subRowColumns: ReadonlyArray<InternalColumnDef>;
  escapeHatch?: (row: Record<string, unknown>) => Record<string, string>;
  validationCoalesce?: ReadonlyArray<ValidationCoalesceRule> | null;
  crossRowValidator?: CrossRowValidationFn;
  allRows?: unknown[];
}

export function useTableValidation({
  parentColumns,
  subRowColumns,
  escapeHatch,
  validationCoalesce,
  crossRowValidator,
  allRows,
}: UseTableValidationParams): RowValidationFn | undefined {
  const validateParentRow = useMemo(
    () => createRowValidator(parentColumns, escapeHatch, validationCoalesce),
    [parentColumns, escapeHatch, validationCoalesce],
  );
  const validateSubRow = useMemo(
    () => createRowValidator(subRowColumns, escapeHatch, validationCoalesce),
    [subRowColumns, escapeHatch, validationCoalesce],
  );

  return useMemo((): RowValidationFn | undefined => {
    if (!validateParentRow && !validateSubRow && !crossRowValidator) return undefined;

    return (row: Record<string, unknown>, rowKey?: string) => {
      const isSubRow = rowKey?.includes('.') ?? false;
      const validator = isSubRow ? validateSubRow : validateParentRow;
      const singleRowErrors = validator?.(row) ?? {};
      const crossRowErrors = crossRowValidator && rowKey && allRows
        ? crossRowValidator(row, rowKey, allRows)
        : {};
      return { ...singleRowErrors, ...crossRowErrors };
    };
  }, [validateParentRow, validateSubRow, crossRowValidator, allRows]);
}
